import {Component, Host, Prop, State, h} from "@stencil/core";
import {notificationsStore as store} from "../stores/notificationsStore";

/** Notification bell component to display number of notifications */
@Component({
	tag: "uxn-notification-bell",
	styleUrl: "notification-bell.scss",
	shadow: false,
})
export class NotificationBell {
	/** Override default connection timeout */
	@Prop() connectionTimeout: number = 30000;
	@State() connectError: boolean = false;

	#timeoutId: ReturnType<typeof setTimeout> | null = null;

	componentDidLoad() {
		this.#timeoutId = setTimeout(() => {
			if (store.hasFailedInitializing !== null) return; // dangling store only returns null props
			this.connectError = true;
			console.error("Failed to connect to notificationsStore");
		}, this.connectionTimeout);
	}

	disconnectedCallback() {
		if (this.#timeoutId) clearTimeout(this.#timeoutId);
		store.reset();
	}

	#getIndicator() {
		if (!this.connectError && store.hasFailedInitializing === null) return "steelblue"; // initial state: before timeout; store might be dangling (only returning null props)
		if (store.isLoading) return "steelblue"; // loading always takes priority over errors to signal that a recovery attempt is in progress
		if (this.connectError || store.hasFailedInitializing || store.hasFailedGet) return "red";
		return null;
	}

	render() {
		const indicator = this.#getIndicator();
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<Host class="uxn-notification-bell">
				<svg
					id="masked-bell-icon"
					class={`ux-icon__svg ${indicator ? "show-indicator" : "hide-indicator"}`}
					width="100%"
					height="100%"
					viewBox="0 0 14 16.5"
					preserveAspectRatio="xMidYMid meet"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
				>
					<defs id="defs1">
						<mask id="indicator-mask" maskUnits="userSpaceOnUse">
							<rect id="background-mask" fill="white" x="0" y="0" width="100%" height="100%" />
							<path
								id="indicator-circle-mask"
								fill="black"
								d="M 3.5722656 2.015625 C 1.6453267 3.3207867 0.578125 5.4408123 0.578125 7.4492188 L 0.578125 8.2246094 A 3.4809999 3.4809999 0 0 0 2.5 8.8066406 A 3.4809999 3.4809999 0 0 0 3.5722656 8.6308594 L 3.5722656 7.4492188 C 3.5722656 6.1663022 4.3134528 4.5535967 5.6953125 3.9453125 A 3.4809999 3.4809999 0 0 0 3.5722656 2.015625 z "
							/>
						</mask>
					</defs>
					<circle id="indicator" class="indicator" fill={indicator ?? ""} cx="2.5" cy="5.3254938" r="2.5" />
					<path
						id="bell"
						mask="url(#indicator-mask)"
						fill="none"
						stroke="currentColor"
						stroke-width="1"
						stroke-linecap="round"
						stroke-linejoin="round"
						d="m 9.4634409,13.484876 c 0.2720481,1.455659 -1.0411735,2.514803 -2.4608252,2.514803 -1.4196884,0 -2.7381309,-1.059144 -2.4660824,-2.514803 M 5.6076319,2.376199 C 3.243972,3.0773627 2.0755608,5.472434 2.0755608,7.4493478 v 3.7200692 c -1.61471788,0 -1.57574878,0.89609 -1.57574878,1.032274 v 1.285467 H 13.500162 v -1.285467 c 0,-0.136182 0.03897,-1.032274 -1.575748,-1.032274 V 7.4493478 c 0,-1.9769134 -1.168412,-4.3719851 -3.5320718,-5.0731488 C 8.6283626,1.4637662 7.9724316,0.48692203 7.0026157,0.48692203 c -0.9698526,0 -1.6310041,0.97684417 -1.3949838,1.88927697 z"
					/>
				</svg>
				{store.count > 0 && <ux-badge>{store.count > 99 ? "99+" : store.count}</ux-badge>}
			</Host>
		);
	}
}
